import { useState, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface ImageUploaderProps {
  images: string[];
  onChange: (images: string[]) => void;
}

const ImageUploader = ({ images, onChange }: ImageUploaderProps) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (fileArray.length === 0) return;

    setUploading(true);
    const uploaded: string[] = [];

    for (const file of fileArray) {
      const ext = file.name.split(".").pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

      setUploadProgress((prev) => ({ ...prev, [fileName]: 0 }));

      const { data, error } = await supabase.storage
        .from("product-images")
        .upload(fileName, file, { upsert: false });

      if (!error && data) {
        const { data: urlData } = supabase.storage
          .from("product-images")
          .getPublicUrl(data.path);
        uploaded.push(urlData.publicUrl);
      }

      setUploadProgress((prev) => {
        const next = { ...prev };
        delete next[fileName];
        return next;
      });
    }

    onChange([...images, ...uploaded]);
    setUploading(false);
  }, [images, onChange]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) uploadFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) uploadFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const removeImage = async (url: string, index: number) => {
    const path = url.split("/product-images/")[1];
    if (path) {
      await supabase.storage.from("product-images").remove([path]);
    }
    onChange(images.filter((_, i) => i !== index));
  };

  const moveImage = (from: number, to: number) => {
    if (to < 0 || to >= images.length) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const pendingCount = Object.keys(uploadProgress).length;

  return (
    <div className="flex flex-col gap-3">
      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all duration-200 ${
          dragOver
            ? "border-[#3a8cbf] bg-[#3a8cbf]/5"
            : "border-gray-200 bg-gray-50 hover:border-[#3a8cbf]/50 hover:bg-[#3a8cbf]/3"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileInput}
          className="hidden"
        />
        {uploading ? (
          <>
            <div className="w-8 h-8 border-3 border-[#3a8cbf]/20 border-t-[#3a8cbf] rounded-full animate-spin" />
            <p className="text-sm text-[#3a8cbf] font-medium">
              Uploading {pendingCount} image{pendingCount !== 1 ? "s" : ""}...
            </p>
          </>
        ) : (
          <>
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#3a8cbf]/10 to-[#e05a8a]/10">
              <i className="ri-image-add-line text-xl text-[#3a8cbf]" />
            </div>
            <p className="text-sm font-medium text-gray-700">
              Drop images here or <span className="text-[#3a8cbf]">click to upload</span>
            </p>
            <p className="text-xs text-gray-400">JPG, PNG, WEBP up to 5MB each — multiple allowed</p>
          </>
        )}
      </div>

      {/* Preview Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {images.map((url, idx) => (
            <div key={url} className="relative group rounded-xl overflow-hidden border border-gray-100 aspect-square bg-gray-50">
              <img src={url} alt={`Product image ${idx + 1}`} className="w-full h-full object-cover" />

              {/* Primary badge */}
              {idx === 0 && (
                <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white">
                  Main
                </span>
              )}

              {/* Actions overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                {/* Move left */}
                {idx > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); moveImage(idx, idx - 1); }}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/90 hover:bg-white text-gray-700 cursor-pointer transition-colors"
                    title="Move left"
                  >
                    <i className="ri-arrow-left-s-line text-sm" />
                  </button>
                )}
                {/* Delete */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeImage(url, idx); }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-500 hover:bg-red-600 text-white cursor-pointer transition-colors"
                  title="Remove"
                >
                  <i className="ri-delete-bin-6-line text-sm" />
                </button>
                {/* Move right */}
                {idx < images.length - 1 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); moveImage(idx, idx + 1); }}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/90 hover:bg-white text-gray-700 cursor-pointer transition-colors"
                    title="Move right"
                  >
                    <i className="ri-arrow-right-s-line text-sm" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {images.length > 0 && (
        <p className="text-xs text-gray-400">
          <i className="ri-information-line mr-1" />
          First image is the main display image. Hover to reorder or remove.
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
